import { forwardRef, useEffect, useCallback, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/cn';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** モーダルの表示状態 */
  open: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** タイトル */
  title?: ReactNode;
  /** 説明文 */
  description?: ReactNode;
  /** モーダルのサイズ（`full` は横幅のみ拡大 / `fullscreen` は 95vw × 95vh でビューポートを占有） */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullscreen';
  /** オーバーレイクリックで閉じる */
  closeOnOverlayClick?: boolean;
  /** ESCキーで閉じる */
  closeOnEsc?: boolean;
  /** フッター */
  footer?: ReactNode;
}

/** @graph-connects none */
const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[95vw] md:max-w-6xl',
  fullscreen: 'max-w-none max-h-none w-[95vw] h-[95vh] rounded-lg',
};

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business 汎用モーダル。sm/md/lg/xl/full/fullscreenサイズ、オーバーレイクリック/Escキー閉じ、ヘッダー・フッタースロット対応のPortalベースダイアログ
 * @graph-connects none
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      title,
      description,
      size = 'md',
      closeOnOverlayClick = true,
      closeOnEsc = true,
      footer,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // ESCキーで閉じる
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (closeOnEsc && e.key === 'Escape') {
          onClose();
        }
      },
      [closeOnEsc, onClose],
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }, [open, handleKeyDown]);

    if (!open) return null;

    const modalContent = (
      <div
        className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-description' : undefined}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={closeOnOverlayClick ? onClose : undefined}
          aria-hidden="true"
        />

        {/* Modal Content */}
        <div
          ref={ref}
          className={cn(
            'relative w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl animate-fade-in-up max-h-[85vh] flex flex-col',
            sizeStyles[size],
            className,
          )}
          {...props}
        >
          {/* Header */}
          {(title != null || description != null) && (
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-[var(--color-border)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  {title && (
                    <h2
                      id="modal-title"
                      className="text-lg font-semibold text-[var(--color-text-primary)]"
                    >
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p
                      id="modal-description"
                      className="mt-1 text-sm text-[var(--color-text-secondary)]"
                    >
                      {description}
                    </p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
                  aria-label="閉じる"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              {footer}
            </div>
          )}
        </div>
      </div>
    );

    return createPortal(modalContent, document.body);
  },
);

Modal.displayName = 'Modal';
